import React, { useState, useEffect, useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE, PROXY_BASE } from '../config';
import {
  normalizeWord,
  wordsMatchFuzzy,
  isKitzur,
  isMemNunVariant,
  getRawTokens
} from '../utils/hebrewUtils';

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`;

const Toolbar = styled.div`
  padding: 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 5px;
  border-radius: 4px;
  border: 1px solid #ced4da;
`;

const Button = styled.button`
  padding: 5px 15px;
  background: ${props => props.disabled ? '#ccc' : (props.variant === 'secondary' ? '#6c757d' : '#28a745')};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  &:hover { background: ${props => props.disabled ? '#ccc' : (props.variant === 'secondary' ? '#5a6268' : '#218838')}; }
  margin-left: 10px;
`;

const MainLayout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 300px;
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SidebarSectionHeader = styled.div`
  padding: 10px;
  background-color: #e9ecef;
  font-weight: bold;
  font-size: 0.9rem;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
`;

const SidebarItem = styled.div`
  padding: 8px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  font-size: 0.9rem;
  background-color: ${props => props.active ? '#e3f2fd' : 'transparent'};
  &:hover { background-color: #f1f1f1; }
  padding-left: 20px;
`;

const Workspace = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow-y: auto;
  direction: rtl;
`;

const AlignmentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  height: 100%;
`;

const TextPanel = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  background: white;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.h3`
  margin-top: 0;
  font-size: 1.1rem;
  color: #555;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  margin-bottom: 15px;
`;

const LinkItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 15px;
  font-size: 0.9rem;
`;

const ColorBadge = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.color};
  display: inline-block;
  margin-left: 5px;
`;

const Word = styled.span`
  display: inline-block;
  padding: 2px 4px;
  border-radius: 3px;
  cursor: pointer;
  margin: 2px;
  transition: all 0.1s;
  
  background-color: ${props => 
    props.isTempStart ? '#4dabf7' : 
    props.isTempSelection ? '#e2e6ea' : 
    props.linkColor ? props.linkColor : 'transparent'};
    
  color: ${props => props.isTempStart ? 'white' : 
           props.linkColor ? '#000' : 
           (props.isShared ? '#b02a37' : 'inherit')};
  
  border: ${props => props.isTempStart ? '2px solid #007bff' : 
                     props.isTempSelection ? '1px dashed #999' : 
                     '1px solid transparent'};
                     
  font-weight: ${props => props.isShared ? 'bolder' : 'normal'};

  &:hover { 
    opacity: 0.8;
    background-color: #f0f0f0;
  }
`;

// Palette for multiple links
const LINK_COLORS = [
    '#ffc9c9', // Red
    '#b2f2bb', // Green
    '#a5d8ff', // Blue
    '#ffec99', // Yellow
    '#eebefa', // Indigo
    '#ffd8a8', // Orange
    '#bac8ff', // Cyan
    '#d8f5a2', // Lime
];

function AnnotationView({ tractate, onTractateChange }) {
  const [tractates, setTractates] = useState([]);
  const [parallels, setParallels] = useState([]);
  const [activeParallel, setActiveParallel] = useState(null);
  const [toseftaData, setToseftaData] = useState(null);
  const [parallelTextContent, setParallelTextContent] = useState("");

  // Temporary Selection State (for creating a NEW link)
  const [selection, setSelection] = useState({
      tosefta: { start: null, end: null },
      parallel: { start: null, end: null }
  });

  // Saved Links State: List of { tosefta: [s, e], parallel: [s, e], color: string }
  const [savedLinks, setSavedLinks] = useState([]);

  const [expandedSections, setExpandedSections] = useState({});

  // Auto-Detection State (moved up to avoid reference before declaration)
  const [hasAutoDetected, setHasAutoDetected] = useState(false);

  // Unsaved changes indicator
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Ref for autoscrolling the parallel text panel
  const parallelPanelRef = React.useRef(null);

  // Align a backend-computed parallel span to the dibur hamatchil using
  // the same tokenization as renderTextWords.
  const adjustParallelSpanToDibur = (parallel, text, origStart, origEnd) => {
    const dibur = parallel?.dibur_hamat_chil;
    if (!dibur || !text || origStart === null || origStart === undefined) {
      return { start: origStart, end: origEnd };
    }

    // Use getRawTokens for consistent tokenization with backend
    const words = getRawTokens(text);
    const diburWords = getRawTokens(dibur);
    if (diburWords.length === 0 || words.length === 0) {
      return { start: origStart, end: origEnd };
    }

    const maxIdx = words.length - diburWords.length;
    if (maxIdx < 0) {
      return { start: origStart, end: origEnd };
    }

    const searchRadius = 40;
    let bestIdx = origStart;
    const from = Math.max(0, origStart - searchRadius);
    const to = Math.min(maxIdx, origStart + searchRadius);

    for (let i = from; i <= to; i += 1) {
      let ok = true;
      for (let j = 0; j < diburWords.length; j += 1) {
        if (!wordsMatchFuzzy(words[i + j], diburWords[j])) {
          ok = false;
          break;
        }
      }
      if (ok) {
        bestIdx = i;
        break;
      }
    }

    const spanLen = (origEnd !== null && origEnd !== undefined && origEnd >= origStart)
      ? (origEnd - origStart)
      : 0;
    const newStart = bestIdx;
    const newEnd = spanLen > 0
      ? Math.min(words.length - 1, newStart + spanLen)
      : bestIdx;

    return { start: newStart, end: newEnd };
  };

  // 1. Load Tractates
  useEffect(() => {
    axios.get(`${API_BASE}/api/tractates`)
      .then(res => setTractates(res.data))
      .catch(err => console.error(err));
  }, []);

  // 2. Load Parallels & Text
  useEffect(() => {
    if (tractate) {
      axios.get(`${API_BASE}/api/parallels/${tractate}?include_text=false`)
        .then(res => {
            const list = res.data.entries || res.data.parallels || [];
            // Add _uiId to each parallel for proper tracking
            const listWithIds = list.map((p, index) => ({ ...p, _uiId: index }));
            setParallels(listWithIds);
            setActiveParallel(null);
            setSelection({ tosefta: { start: null, end: null }, parallel: { start: null, end: null } });
            setSavedLinks([]);
            setHasUnsavedChanges(false);
        })
        .catch(err => {
            console.error(err);
            alert(`Error loading parallels: ${err.message || 'Unknown error'}`);
        });

      axios.get(`${API_BASE}/api/text/${tractate}`)
        .then(res => setToseftaData(res.data))
        .catch(err => {
            console.error(err);
            alert(`Error loading Tosefta text: ${err.message || 'Unknown error'}`);
        });
    }
  }, [tractate]);

  // Group Parallels
  const groupedParallels = useMemo(() => {
    const groups = {};
    parallels.forEach(p => {
        const parts = p.tosefta_ref.split(' ');
        const refKey = parts[parts.length - 1];
        if (!groups[refKey]) groups[refKey] = [];
        groups[refKey].push(p);
    });
    return groups;
  }, [parallels]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({...prev, [key]: !prev[key]}));
  };

  // 3. Handle Selection
  const handleSelectParallel = (p) => {
    setActiveParallel(p);
    setParallelTextContent("Loading...");
    setSelection({ tosefta: { start: null, end: null }, parallel: { start: null, end: null } });
    setHasAutoDetected(false); // Reset detection state
    
    // Load existing links if present
    if (p.links && Array.isArray(p.links) && p.links.length > 0) {
        const linksWithColor = p.links.map((link, idx) => ({
            ...link,
            color: LINK_COLORS[idx % LINK_COLORS.length]
        }));
        setSavedLinks(linksWithColor);
        setHasAutoDetected(true); // Already has links
    } else {
        // Check for manual annotation first; ignore precomputed DB spans here
        if (p.manual_tosefta_start_word !== undefined) {
             setSavedLinks([{
                 tosefta: [p.manual_tosefta_start_word, p.manual_tosefta_end_word],
                 parallel: [p.manual_parallel_start_word, p.manual_parallel_end_word],
                 color: LINK_COLORS[0],
                 auto: false,
             }]);
             setHasAutoDetected(true);
        } else {
            setSavedLinks([]);
            // Will trigger auto-detection in useEffect
        }
    }

    // Fetch Text
    if (p.parallel_text_raw || p.parallel_text) {
        setParallelTextContent(p.parallel_text_raw || p.parallel_text);
    }
    else {
        const encodedRef = encodeURIComponent(p.parallel_ref);
        axios.get(`${PROXY_BASE}/proxy/sefaria/${encodedRef}`)
            .then(res => {
                const heText = res.data.he;
                let text = "";
                if (Array.isArray(heText)) text = heText.flat(Infinity).join(" ");
                else if (typeof heText === 'string') text = heText;
                else text = "Text unavailable";
                setParallelTextContent(text);
            })
            .catch(err => {
                console.error(err);
                setParallelTextContent("Error loading text");
            });
    }
  };


  // Auto-detection using backend auto-span endpoint
  useEffect(() => {
      if (activeParallel && savedLinks.length === 0 && !hasAutoDetected && activeParallel._uiId !== undefined) {
          const pid = activeParallel._uiId;

          axios.get(`${API_BASE}/api/parallels/${tractate}/${pid}/auto-span`)
              .then(res => {
                  console.debug("AnnotationView auto-span result", res.data);

                  const tStart = res.data.source_start;
                  const tEnd = res.data.source_end;
                  const pStart = res.data.target_start;
                  const pEnd = res.data.target_end;

                  // Only set if we got valid results
                  if (pStart !== null && pEnd !== null && tStart !== null && tEnd !== null) {
                      setSavedLinks([{
                          tosefta: [tStart, tEnd],
                          parallel: [pStart, pEnd],
                          color: LINK_COLORS[0],
                          auto: true,
                      }]);
                  }

                  setHasAutoDetected(true);
              })
              .catch(err => {
                  console.error("Auto-span error:", err);
                  setHasAutoDetected(true); // Mark as attempted even on error
              });
      }
  }, [activeParallel, savedLinks, hasAutoDetected, tractate]);

  // Once we have auto-detected links and the parallel text, align the
  // parallel span to the dibur hamatchil using the same tokenization
  // that the annotation view uses, to avoid index drift between backend
  // and frontend.
  useEffect(() => {
      if (!activeParallel || !parallelTextContent) return;
      if (savedLinks.length !== 1) return;

      const link = savedLinks[0];
      if (!link.auto) return;

      const [pStart, pEnd] = link.parallel;
      if (pStart === null || pStart === undefined) return;

      const adjusted = adjustParallelSpanToDibur(activeParallel, parallelTextContent, pStart, pEnd);
      if (adjusted.start === pStart && adjusted.end === pEnd) {
          // No change needed; mark as non-auto to prevent re-running
          setSavedLinks([{ ...link, auto: false }]);
          return;
      }

      setSavedLinks([{
          ...link,
          parallel: [adjusted.start, adjusted.end],
          auto: false,
      }]);
      // This adjustment is internal; don't mark as unsaved change yet.
  }, [activeParallel, parallelTextContent, savedLinks, adjustParallelSpanToDibur]);

  // Autoscroll the parallel text panel so that the beginning of the
  // first saved parallel span is brought into view.
  useEffect(() => {
      if (!parallelPanelRef.current) return;
      if (!savedLinks || savedLinks.length === 0) return;

      // Find the earliest parallel start index across links
      const starts = savedLinks
        .map(l => l.parallel && l.parallel[0])
        .filter(v => v !== null && v !== undefined);
      if (starts.length === 0) return;

      const targetIdx = Math.min(...starts);
      const el = parallelPanelRef.current.querySelector(`[data-idx="${targetIdx}"]`);
      if (!el) return;

      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } catch (e) {
        // scrollIntoView may not be supported in all environments; ignore
      }
  }, [savedLinks, parallelTextContent]);

  // 4. Logic for Adding Links
  const handleAddLink = () => {
      const tSel = selection.tosefta;
      const pSel = selection.parallel;

      if (tSel.start === null || pSel.start === null) {
          alert("Please select a range in both texts first.");
          return;
      }

      const newLink = {
          tosefta: [Math.min(tSel.start, tSel.end ?? tSel.start), Math.max(tSel.start, tSel.end ?? tSel.start)],
          parallel: [Math.min(pSel.start, pSel.end ?? pSel.start), Math.max(pSel.start, pSel.end ?? pSel.start)],
          color: LINK_COLORS[savedLinks.length % LINK_COLORS.length]
      };

      setSavedLinks([...savedLinks, newLink]);
      setHasUnsavedChanges(true);

      // Clear temp selection
      setSelection({ tosefta: { start: null, end: null }, parallel: { start: null, end: null } });
  };

  const handleRemoveLink = (index) => {
      const newLinks = [...savedLinks];
      newLinks.splice(index, 1);
      setSavedLinks(newLinks);
      setHasUnsavedChanges(true);
  };

  // 5. Save to Backend
  const handleSaveAnnotation = () => {
      if (!activeParallel) return;

      let tStart = null, tEnd = null, pStart = null, pEnd = null;

      if (savedLinks.length > 0) {
          tStart = Math.min(...savedLinks.map(l => l.tosefta[0]));
          tEnd = Math.max(...savedLinks.map(l => l.tosefta[1]));
          pStart = Math.min(...savedLinks.map(l => l.parallel[0]));
          pEnd = Math.max(...savedLinks.map(l => l.parallel[1]));
      }

      const linksToSave = savedLinks.map(l => ({
          tosefta: l.tosefta,
          parallel: l.parallel
      }));

      const payload = {
          parallel_ui_id: activeParallel._uiId,
          tosefta_start_word: tStart,
          tosefta_end_word: tEnd,
          parallel_start_word: pStart,
          parallel_end_word: pEnd,
          links: linksToSave
      };

      axios.put(`${API_BASE}/api/parallels/${tractate}/update`, payload)
        .then(res => {
            alert("Saved successfully!");
            const updated = {
                ...activeParallel,
                tosefta_start_word: tStart, tosefta_end_word: tEnd,
                parallel_start_word: pStart, parallel_end_word: pEnd,
                links: linksToSave
            };
            // Fix: use _uiId property to match, not array index
            setParallels(prev => prev.map(item => item._uiId === activeParallel._uiId ? updated : item));
            setActiveParallel(updated);
            setHasUnsavedChanges(false);
        })
        .catch(err => {
            console.error(err);
            alert(`Error saving annotation: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
        });
  };



  const getActiveHalakhaText = () => {
    if (!activeParallel || !toseftaData || !toseftaData.text) return "";
    try {
        const parts = activeParallel.tosefta_ref.split(' ');
        const refNum = parts[parts.length - 1];
        const [chap, hal] = refNum.split(':').map(n => parseInt(n, 10));
        return toseftaData.text[chap - 1][hal - 1] || "";
    } catch (e) { return ""; }
  };

  const handleWordClick = (type, idx) => {
      setSelection(prev => {
          const current = prev[type];
          if (current.start === null) {
              return { ...prev, [type]: { start: idx, end: null } };
          } else if (current.end === null) {
              const start = Math.min(current.start, idx);
              const end = Math.max(current.start, idx);
              return { ...prev, [type]: { start, end } };
          } else {
              return { ...prev, [type]: { start: idx, end: null } };
          }
      });
  };

  const renderTextWords = (text, type, otherText) => {
    if (!text) return null;
    // Use getRawTokens for consistent tokenization with backend
    const rawTokens = getRawTokens(text);
    const normTokens = rawTokens.map(w => normalizeWord(w));

    const otherRawTokens = getRawTokens(otherText || '');
    const otherWordsList = otherRawTokens.map(w => normalizeWord(w)).filter(w => w.length > 0);
    const otherWordsSet = new Set(otherWordsList);

    // Current Temp Selection
    const sel = selection[type];

    return (
        <div
          style={{lineHeight: '2', fontSize: '1.2rem', fontFamily: 'SBL Hebrew'}}
          ref={type === 'parallel' ? parallelPanelRef : null}
        >
            {normTokens.map((normToken, idx) => {
                const word = rawTokens[idx] || normToken;
                let isTempStart = false;
                let isTempSelection = false;

                // Shared Word Logic - use imported fuzzy matching functions
                const normalized = normToken;
                let isShared = false;
                if (normalized.length > 1) {
                    if (otherWordsSet.has(normalized)) isShared = true;
                    else isShared = otherWordsList.some(o => isMemNunVariant(normalized, o) || isKitzur(normalized, o));
                }

                // Temp Selection Highlight
                if (sel.start !== null) {
                    if (idx === sel.start || idx === sel.end) isTempStart = true;
                    if (sel.end !== null && idx >= sel.start && idx <= sel.end) isTempSelection = true;
                }

                // Saved Links Highlight
                let linkColor = null;
                // Check if index is in any saved link for this type
                const foundLink = savedLinks.find(l => {
                    const range = l[type];
                    if (!range || range[0] === null || range[1] === null) return false;
                    return idx >= range[0] && idx <= range[1];
                });
                if (foundLink) linkColor = foundLink.color;

                return (
                    <Word 
                        key={idx} 
                        data-idx={idx}
                        isTempStart={isTempStart}
                        isTempSelection={isTempSelection}
                        linkColor={linkColor}
                        isShared={isShared}
                        onClick={() => handleWordClick(type, idx)}
                    >
                        {word}
                    </Word>
                );
            })}
        </div>
    );
  };

  return (
    <Container>
      <Toolbar>
        <label>Tractate:</label>
        <Select value={tractate} onChange={e => onTractateChange(e.target.value)}>
          {tractates.map(t => <option key={t} value={t}>{t}</option>)}
        </Select>
        <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px'}}>
            {hasUnsavedChanges && (
              <div style={{
                color: '#856404',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: 'bold'
              }}>
                ⚠ Unsaved Changes
              </div>
            )}
            {savedLinks.map((l, i) => (
                <LinkItem key={i}>
                    <ColorBadge color={l.color} /> Link {i+1}
                    <span style={{marginLeft: '5px', cursor: 'pointer', color: 'red'}} onClick={() => handleRemoveLink(i)}>×</span>
                </LinkItem>
            ))}
            <Button onClick={handleAddLink} disabled={selection.tosefta.start === null || selection.parallel.start === null}>
                + Add Link
            </Button>
            <Button onClick={handleSaveAnnotation} disabled={!hasUnsavedChanges}>Save Annotation</Button>
        </div>
      </Toolbar>

      <MainLayout>
        <Sidebar>
            {Object.entries(groupedParallels).map(([section, items]) => {
                // Parse chapter from refKey (e.g., "1:1" -> "1")
                const chapter = section.split(':')[0];
                return (
                    <div key={section}>
                        <SidebarSectionHeader onClick={() => toggleSection(section)}>
                            <span>פרק {chapter} הלכה {section}</span>
                            <span>{expandedSections[section] ? '▼' : '▶'}</span>
                        </SidebarSectionHeader>
                        {expandedSections[section] && items.map((p, idx) => (
                            <SidebarItem
                                key={idx}
                                active={activeParallel?._uiId === p._uiId}
                                onClick={() => handleSelectParallel(p)}
                            >
                                {p.parallel_source}: {p.parallel_display_ref}
                            </SidebarItem>
                        ))}
                    </div>
                );
            })}
        </Sidebar>

        <Workspace>
            {activeParallel ? (
                <AlignmentGrid>
                    <TextPanel>
                        <PanelHeader>Tosefta ({activeParallel.tosefta_ref})</PanelHeader>
                        {renderTextWords(getActiveHalakhaText(), 'tosefta', parallelTextContent)}
                    </TextPanel>
                    <TextPanel>
                        <PanelHeader>{activeParallel.parallel_source}: {activeParallel.parallel_display_ref}</PanelHeader>
                        {renderTextWords(parallelTextContent, 'parallel', getActiveHalakhaText())}
                    </TextPanel>
                </AlignmentGrid>
            ) : (
                <div style={{textAlign: 'center', marginTop: '100px', color: '#999'}}>
                    Select a parallel from the sidebar to begin annotation.
                </div>
            )}
        </Workspace>
      </MainLayout>
    </Container>
  );
}

export default AnnotationView;
